import { SidebarContainer } from '@affine/core/modules/app-sidebar/views';
import clsx from 'clsx';
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

import {
  type WorkspaceAppSidebarElementProps,
  WorkspaceAppSidebarFrame,
} from '../workspace-app-sidebar';
import { bottomContainer, sidebarContentPanel } from './index.css';
import * as railStyles from './ui-redesign-rail.css';

export interface NavigationOverlayPaneProps {
  children: ReactNode;
  contentClassName?: string;
  contentProps?: WorkspaceAppSidebarElementProps;
  footer?: ReactNode;
  /** Stays in place above the scrolling content. */
  header: ReactNode;
  rootProps?: WorkspaceAppSidebarElementProps;
  testId: string;
  twoPane?: boolean;
}

interface OpenState {
  mobileOpen: boolean;
  open: boolean;
}

const NavigationOverlayPaneOpenContext = createContext<OpenState | null>(null);

/**
 * Whether the pane inside is open. Only the pane's frame reads it: opening or closing the pane
 * renders that frame anew and nothing the pane lists.
 */
export function NavigationOverlayPaneOpenState({
  children,
  mobileOpen,
  open,
}: OpenState & { children: ReactNode }): ReactElement {
  const state = useMemo(() => ({ mobileOpen, open }), [mobileOpen, open]);
  return (
    <NavigationOverlayPaneOpenContext.Provider value={state}>
      {children}
    </NavigationOverlayPaneOpenContext.Provider>
  );
}

/** Shared shell for rail-owned secondary navigation panes. */
export function NavigationOverlayPane({
  children,
  contentClassName,
  contentProps,
  footer,
  header,
  rootProps,
  testId,
  twoPane = false,
}: NavigationOverlayPaneProps): ReactElement {
  const state = useContext(NavigationOverlayPaneOpenContext);
  if (!state) {
    throw new Error(
      'NavigationOverlayPane must be rendered inside NavigationOverlayPaneOpenState'
    );
  }
  const { mobileOpen, open } = state;
  return (
    <WorkspaceAppSidebarFrame
      as="div"
      className={railStyles.docsPanel}
      contentClassName={clsx(
        sidebarContentPanel,
        railStyles.docsPanelContent,
        contentClassName
      )}
      contentProps={contentProps}
      footer={
        footer === undefined ? undefined : (
          <SidebarContainer className={bottomContainer}>
            {footer}
          </SidebarContainer>
        )
      }
      header={header}
      rootProps={{
        ...rootProps,
        'data-open': open,
        'data-mobile-open': mobileOpen,
        'data-two-pane': twoPane,
        'data-testid': testId,
        inert: open || twoPane ? undefined : true,
      }}
    >
      {children}
    </WorkspaceAppSidebarFrame>
  );
}
